<template>
  <div ref="sliderListRef">
    <div v-if="showSliderList">
      <CommonSliderDrawer :openDrawer="open">
        <div class="absolute right-0 m-2 flex">
          <button
            @click="closeAllModal"
            type="button"
            class="z-50 text-gray-400 bg-light-silver rounded-full fill-secondary p-1"
          >
            <svg aria-hidden="true" class="w-5 h-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="sr-only">Close modal</span>
          </button>
        </div>
        <div
          v-if="route.query.slug && serviceType.slug != 'everywhere-nurse-service'"
          class="py-20 px-6 h-full bg-grey-light flex flex-col shadow-[-3px_0px_6px_rgba(0,0,0,0.29)]"
        >
          <h3 class="text-center">{{ $t("testDetail.chooseClinic") }}</h3>
          <div class="flex flex-col items-center">
            <div class="flex px-3.5 w-full my-12 md:px-8 py-4 border-light-silver bg-white">
              <NuxtImg
                loading="lazy"
                src="/icons/clinic-icon.svg"
                class="w-9"
                alt="clinic icon"
                width="36"
                height="36"
              />
              <div class="px-2">
                <div class="flex items-baseline">
                  <div class="basis-1/2 grow">
                    <h3 class="cursor-pointer">
                      {{ selectedClinic.display_title }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
            <BaseButton @click="saveSelection()" :text="$t('testDetail.bookClinic')" class="w-[220px] h-12 text-lg" />

            <h2 class="my-8">{{ $t("common.or") }}</h2>
            <BaseButton
              variant="outlined"
              weight="normal"
              @click="saveSelection('chooseClinic')"
              :text="$t('testDetail.chooseAnotherClinic')"
              class="w-[220px] h-12 text-lg bg-white"
            />
          </div>
        </div>

        <div v-else class="flex h-full flex-col shadow-[-3px_0px_6px_rgba(0,0,0,0.29)]">
          <div
            class="bg-grey-light relative top-0 z-10 md:max-w-[390px] w-full"
            :class="{
              'border-b border-light-silver': showClose && clinicLists.length > 0,
            }"
          >
            <div class="p-5">
              <h3 class="mr-2">
                {{ $t("clinic.sliderTitle", { testName: testName }) }}
              </h3>
            </div>
            <div class="md:mx-3">
              <ClinicMapSearchBox
                :tabValue="true"
                :showClose="showClose"
                :showSearchBox="true"
                @onCloseClick="onCloseClicked"
                @onSearchClick="onSearchClicked"
                :isMobile="true"
                :searchTextValue="mapSearchText"
              ></ClinicMapSearchBox>
              <ClinicTabView
                :tabValue="false"
                v-if="showClose"
                @onTabChange="(data) => (showGoogleMap = data)"
              ></ClinicTabView>
            </div>
          </div>

          <div
            :class="clinicLists.length > 0 && showClose ? 'bg-white overflow-y-auto' : 'bg-grey-light'"
            class="h-full"
          >
            <div v-if="clinicLists.length == 0 && showClose">
              <SkeletonClinic v-for="index in 10" :key="index"></SkeletonClinic>
            </div>
            <ClinicMapRegionList
              v-if="!showGoogleMap"
              :clinicLists="clinicLists"
              :isMobile="isMobile"
              :showClose="showClose"
              sliderHeight
            />
          </div>
          <ClientOnly>
            <div class="bg-white pb-6" :class="!showGoogleMap ? 'absolute' : ''">
              <ClinicGoogleMap
                :mapheight="isMobile ? 'calc(100vh - 300px)' : 'calc(100vh - 275px)'"
                @getClinicsList="setClinicLists"
                :testId="testId"
                :city="mapSearchText"
                :isMobile="isMobile"
                :showGoogleMap="showGoogleMap"
              />
            </div>
          </ClientOnly>
        </div>
        <div
          class="border-t-2 border-light-silver w-full bg-grey-light absolute bottom-0 p-3 flex justify-center md:justify-start text-secondary underline font-bold"
        >
          <NuxtLink @click="closeAllModal" class="cursor-pointer">{{ $t("common.cancel") }}</NuxtLink>
        </div>
        <!-- /End replace -->
      </CommonSliderDrawer>
    </div>
    <div v-else>
      <CommonPanel
        v-if="open"
        @onBackClick="setCheckoutPanel(false)"
        @openCheckPanel="openChosenTestsPanel"
        :showModal="showTestLists"
        startPoint="tests"
      />

      <ClinicCheckoutPanel
        @chooseTest="chooseTestSelection"
        @closeTestModal="setTestPanel(false)"
        @closeModal="removeClinicSelected"
        @closeCheckoutModal="showChosenTests ? closeChosenTestsPanel() : closeAllModal()"
        :openDrawer="open"
        :serviceType="serviceType"
        :otherServiceLists="otherServiceLists"
        @onCloseClicked="onCloseClicked"
        @removeClinic="removeClinicSelected"
        :showRemoveClinicOption="true"
      ></ClinicCheckoutPanel>

      <!-- Used show test on `Test` page -->
      <ClinicCheckoutPanel
        @chooseTest="chooseTestSelection"
        @removeClinic="removeClinicSelected"
        @closeModal="closeChosenTestsPanel"
        @closeCheckoutModal="closeChosenTestsPanel"
        :openDrawer="showChosenTests"
        :serviceType="serviceType"
        :otherServiceLists="otherServiceLists"
        :showRemoveClinicOption="true"
      />
    </div>
  </div>
</template>

<script setup>
import { useGlobalStore } from "@/stores/global";
import { useMapStore } from "@/stores/map";
const globalStore = useGlobalStore();
const showGlobalSearchText = computed(() => globalStore.siteClinicMapText);

const { t } = useI18n();
const emit = defineEmits(["closeModal", "searchText", "clearSearch", "onTabClick"]);
const picked = ref();
const clinicDetail = ref();

const sliderListRef = ref(null);
let ignoreClick = false; // Flag to ignore clicks for a short duration

const props = defineProps({
  openDrawer: {
    type: Boolean,
    default: false,
  },
  testId: {
    type: Number,
  },
  testName: {
    type: String,
  },
  serviceType: {
    type: Object,
  },
  otherServiceLists: {
    type: Object,
  },
});
const clinicFormatedDetail = ref();
const route = useRoute();

const isClinicPath = ref(route.path.includes("clinics"));
const isMobile = useScreenSize("sm");
const open = ref(props.openDrawer);
const showClose = ref(false);
const showTestLists = ref(false);
const mapSearchText = ref();
const clinicLists = ref([]);
const showGoogleMap = ref();
const mapStore = useMapStore();
const selectedClinic = ref();
const showChosenTests = ref(false);

import { useCheckoutStore } from "~/stores/checkout";
const checkoutStore = useCheckoutStore();
const clinicTest = computed(() => mapStore.getSelectedClinic);
const maxTestList = checkoutStore.maxTestLists;
const showSliderList = computed(() => !route.query.slug && props.serviceType.slug != "everywhere-nurse-service");

watch(
  () => props.openDrawer,
  (newData) => {
    // mapSearchText.value = isClinicPath.value ? showGlobalSearchText.value : "";

    showGoogleMap.value = false;
    open.value = newData;
    showTestLists.value = false;

    if (newData) {
      ignoreClick = true;
      setTimeout(() => {
        ignoreClick = false;
      }, 200); // Delay for 200ms
    }
  },
  { immediate: true }
);
const getClinicDetail = computed(() => mapStore.selectedClinic);

watch(
  () => route.query.slug,
  async (newData) => {
    const isEverywhereService = props.serviceType.slug === "everywhere-nurse-service";
    if (isEverywhereService || (newData && !isEverywhereService)) {
      return await fetchTestLists(newData);
    }
    setTestPanel(false);
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

const handleClickOutside = (event) => {
  if (ignoreClick) return; // Ignore the first click after opening
  if (open.value && sliderListRef.value && !sliderListRef.value.contains(event.target)) {
    if (showChosenTests.value && showTestLists.value) {
      closeChosenTestsPanel();
      return;
    }

    closeAllModal();
  }
};

function closeAllModal() {
  onCloseClicked();
  //removeClinicSelected();
  showTestLists.value = false;
  emit("closeModal");
}

function chooseTestSelection() {
  showTestLists.value = true;
}

function setTestPanel(data) {
  showTestLists.value = data;
}

function setCheckoutPanel(data) {
  setTestPanel(data);
}

async function fetchTestLists(newData) {
  mapStore.setPanelTestsLoading(true);
  try {
    let clinicId = newData == "everywhere-nurse-service" ? newData : getClinicDetail.value.id;
    if (clinicId == "everywhere-nurse-service") {
      mapStore.setSelectedClinic({
        id: newData,
        title: "Everywhere Nurse Service",
        slug: "everywhere",
      });
    }
    const { data: productsList } = await useFetchClinicTests(
      clinicId,
      newData != "everywhere-nurse-service" ? "clinic-test" : ""
    );
    await mapStore.getTests(productsList.value?.data ?? []);
  } finally {
    mapStore.setPanelTestsLoading(false);
  }
}

const saveSelection = (type) => {
  switch (type) {
    case "chooseClinic":
      mapStore.reset();
      return navigateTo({ path: route.path });

    default:
      checkoutStore.setSelectedClinicCookieData(selectedClinic.value.id, props.testId, "tests");

      return navigateTo(
        localeUrlQuery({
          path: "/checkout",
          query: { clinic_id: selectedClinic.value.id, test_id: props.testId },
        })
      );
  }
};

// on search clicked
function onSearchClicked(data) {
  if (data) {
    mapSearchText.value = data;
    showClose.value = true;
  }
}

function removeClinicSelected() {
  // setCheckoutPanel(false)
  return navigateTo({
    path: route.path,
  });
}
// on Close Clicked
function onCloseClicked() {
  mapSearchText.value = "";
  showGoogleMap.value = false;
  showClose.value = false;
  clinicLists.value = [];
}

function setClinicLists(data) {
  if (mapSearchText.value) {
    clinicLists.value = data;
  } else {
    clinicLists.value = [];
  }
}

function closeChosenTestsPanel() {
  showChosenTests.value = false;
}

function openChosenTestsPanel() {
  showChosenTests.value = true;
}
</script>
